/**
 * The right-hand HUD column: build/research queue, sector events, missions.
 */

import { useState } from 'react';
import {
  AlertCard,
  AsteroidIcon,
  BuildQueue,
  Button,
  BuildingIcon,
  CometAlert,
  DefenseIcon,
  FleetRow,
  Panel,
  PremiumConfirm,
  ProgressBar,
  QueueItem,
  ResearchIcon,
  ShipyardIcon,
  Stat,
  StatGrid,
  Toast,
  ToastStack,
  formatAmount,
  formatCoordinates,
  formatDurationShort,
} from '@spacegame/ui';
import {
  HARVEST_TRIP_MS,
  MISSIONS,
  activeMission,
  buildingLevel,
  completionCost,
  type FleetMission,
} from '@spacegame/engine';
import { useGame, useGameActions } from '../game/GameProvider';
import { useSettings, useSettingsActions } from '../game/SettingsProvider';
import { useActivityFeed } from '../game/useActivity';
import { allJobs, fleetCapacityStatus, jobLabel } from '../game/selectors';

const KIND_ICON = {
  building: <BuildingIcon size={15} />,
  research: <ResearchIcon size={15} />,
  ship: <ShipyardIcon size={15} />,
  defense: <DefenseIcon size={15} />,
} as const;

export function QueuePanel() {
  const { game, clock } = useGame();
  const actions = useGameActions();
  const settings = useSettings();
  const settingsActions = useSettingsActions();
  const jobs = allJobs(game);
  // The job the player asked to finish with crystals, pending confirmation.
  const [confirming, setConfirming] = useState<number | null>(null);

  const pendingJob = confirming !== null
    ? jobs.find(({ job }) => job.id === confirming)?.job ?? null
    : null;
  const pendingRemaining = pendingJob ? Math.max(0, pendingJob.finishesAt - clock()) : 0;

  return (
    <Panel title="Aktive Warteschlange" eyebrow="Bau & Forschung" corners>
      <BuildQueue slots={{ used: game.planets[0]!.buildQueue.length, total: 3 }}>
        {jobs.map(({ job, planetName }) => {
          const pending = job.startedAt === 0;
          const remaining = pending ? 0 : Math.max(0, job.finishesAt - clock());
          return (
            <QueueItem
              key={job.id}
              name={planetName ? `${jobLabel(job)} · ${planetName}` : jobLabel(job)}
              level={job.kind === 'building' || job.kind === 'research' ? job.amount : undefined}
              count={job.kind === 'ship' || job.kind === 'defense' ? job.amount : undefined}
              kind={job.kind}
              icon={KIND_ICON[job.kind]}
              startedAt={pending ? clock() : job.startedAt}
              finishesAt={pending ? clock() : job.finishesAt}
              pending={pending}
              // The full price to finish, never a fraction — and pressing it
              // only opens the confirmation, it never spends.
              finishCost={pending ? undefined : completionCost(remaining)}
              onBoost={
                pending
                  ? undefined
                  : () => {
                      // The player opted out of this confirmation in Settings —
                      // spend immediately rather than show a dialog they asked
                      // not to see.
                      if (settings.skipBoostConfirm) actions.boostJob(job.id);
                      else setConfirming(job.id);
                    }
              }
              onCancel={() => actions.cancelJob(job.id)}
            />
          );
        })}
      </BuildQueue>

      {pendingJob ? (
        <PremiumConfirm
          open
          subject={
            <>
              <strong className="text-content">{jobLabel(pendingJob)}</strong> sofort fertigstellen.
              Verbleibende Bauzeit: {formatDurationShort(pendingRemaining / 1000)}.
            </>
          }
          effect="Der Auftrag ist unmittelbar abgeschlossen — die gesamte Restzeit entfällt."
          cost={completionCost(pendingRemaining)}
          balance={game.resources.chronos}
          onConfirm={() => {
            actions.boostJob(pendingJob.id);
            setConfirming(null);
          }}
          onCancel={() => setConfirming(null)}
          skipNextTime={{
            checked: settings.skipBoostConfirm,
            onChange: settingsActions.setSkipBoostConfirm,
          }}
        />
      ) : null}
    </Panel>
  );
}

export function EventPanel() {
  const { game, log } = useGame();
  const actions = useGameActions();
  const event = game.event;
  const miners = game.planets[0]!.ships.minerShip;
  const fleetCapacity = fleetCapacityStatus(game);

  return (
    <Panel title="System-Ereignisse" eyebrow={`Sektor ${game.planets[0]!.ring}:${game.planets[0]!.system}`}>
      <div className="space-y-2">
        {event?.type === 'comet' ? (
          <CometAlert
            impactAt={event.deadline}
            onIntercept={() => actions.interceptComet()}
            onShield={() => actions.shieldComet()}
            interceptDisabled={game.planets[0]!.defense.interceptor < 1}
          />
        ) : null}

        {event?.type === 'raid' ? (
          <AlertCard
            severity="danger"
            urgent
            title="Piraten-Überfall im Anflug"
            deadline={event.deadline}
            description={
              `${Object.values(event.fleet ?? {}).reduce((a, b) => a + (b ?? 0), 0)} feindliche Einheiten. ` +
              'Die Schlacht wird automatisch gegen deine Abwehr und stationierte Flotte ausgetragen — ' +
              'baue jetzt Verteidigung, sonst wird geplündert.'
            }
          />
        ) : null}

        {event?.type === 'asteroid' ? (
          <AlertCard
            severity="warning"
            title="Erz-Asteroid im Orbit"
            description={`Geschätzter Ertrag: ${formatAmount(event.size)} Rohstoffe. Löst sich bei Ablauf auf.`}
            deadline={event.deadline}
            actions={
              <Button
                variant="secondary"
                size="sm"
                disabled={miners < 1 || fleetCapacity.full}
                onClick={() => actions.harvestAsteroid(miners)}
                title={
                  miners < 1
                    ? 'Keine Erz-Sammelschiffe im Hangar'
                    : fleetCapacity.full
                      ? `Schiffshangar-Kapazität erreicht (${fleetCapacity.active}/${fleetCapacity.capacity} Flotten unterwegs)`
                      : undefined
                }
              >
                {miners < 1
                  ? 'Keine Sammelschiffe'
                  : game.fleetMissions.some((entry) => entry.kind !== 'campAttack')
                    ? 'Flotte unterwegs'
                    : fleetCapacity.full
                      ? 'Hangar-Kapazität erreicht'
                      : `${miners} Sammelschiffe senden`}
              </Button>
            }
          />
        ) : null}

        {!event && log.length === 0 ? (
          <p className="text-xs leading-relaxed text-content-muted">
            Keine aktiven Ereignisse. Das Backend generiert alle 4–8 Stunden ein Sektor-Ereignis.
          </p>
        ) : null}

        {/*
          Casino outcomes are excluded here — CasinoView now runs its own
          multi-second reel/wheel/scanner reveal, and this panel updates the
          instant the (already-decided) result lands in the log, which would
          spoil every spin before the animation gets anywhere near the end.
        */}
        {log
          .filter((entry) => entry.type !== 'casino')
          .slice(0, 5)
          .map((entry, index) => (
          <AlertCard
            key={`${entry.at}-${index}`}
            severity={
              entry.type === 'cometImpact' ||
              entry.type === 'raidLost' ||
              entry.type === 'scoutLost'
                ? 'danger'
                : entry.type === 'levelUp' ||
                    entry.type === 'harvestReturned' ||
                    entry.type === 'raidRepelled' ||
                    entry.type === 'missionCompleted' ||
                    entry.type === 'blueprint' ||
                    entry.type === 'expeditionReturned'
                  ? 'success'
                  : 'info'
            }
            title={logTitle(entry.type)}
            description={entry.message}
          />
        ))}
      </div>
    </Panel>
  );
}

function logTitle(type: string): string {
  switch (type) {
    case 'jobCompleted': return 'Auftrag abgeschlossen';
    case 'levelUp': return 'Levelaufstieg';
    case 'eventSpawned': return 'Sektor-Ereignis';
    case 'cometResolved': return 'Bedrohung abgewehrt';
    case 'cometImpact': return 'Einschlag';
    case 'asteroidExpired': return 'Asteroid aufgelöst';
    case 'harvestReturned': return 'Flotte zurück';
    case 'raidRepelled': return 'Überfall abgewehrt';
    case 'raidLost': return 'Kolonie geplündert';
    case 'marketRefreshed': return 'Neue Handelsangebote';
    case 'trade': return 'Handel';
    case 'casino': return 'Casino';
    case 'blueprint': return 'Bauplan erbeutet';
    case 'campsRefreshed': return 'Neue Lager erfasst';
    case 'scoutReport': return 'Spionagebericht';
    case 'scoutLost': return 'Sonden verloren';
    case 'expeditionResolved': return 'Gefecht ausgetragen';
    case 'expeditionReturned': return 'Flotte zurück';
    case 'missionCompleted': return 'Mission abgeschlossen';
    default: return 'Ereignis';
  }
}

function isHarvestMission(entry: FleetMission): entry is Extract<FleetMission, { kind: 'harvestAsteroid' | 'harvestDebris' }> {
  return entry.kind === 'harvestAsteroid' || entry.kind === 'harvestDebris';
}

export function MissionPanel() {
  const { game } = useGame();
  // Only harvest fleets are shown here — campAttack has its own display in
  // CampsView, and playerAttack (PvP) isn't tracked in this panel yet.
  const mission = game.fleetMissions.find(isHarvestMission);
  if (!mission) return null;

  const phalanx = buildingLevel(game, 'phalanx');
  return (
    <Panel title="Flottenbewegungen" eyebrow={phalanx > 0 ? `Subraum-Phalanx Stufe ${phalanx}` : 'Ohne Phalanx-Abdeckung'}>
      <FleetRow
        mission="harvest"
        origin={formatCoordinates(game.planets[0]!.ring, game.planets[0]!.system, game.planets[0]!.position)}
        target="Asteroid"
        ships={mission.ships}
        departsAt={mission.returnsAt - HARVEST_TRIP_MS}
        arrivesAt={mission.returnsAt}
      />
    </Panel>
  );
}

/**
 * Orbital debris left by battles — the loop that turns a repelled raid into
 * a reason to own Erz-Sammelschiffe (GDD §3 Trümmerfeld).
 */
export function DebrisPanel() {
  const { game } = useGame();
  const actions = useGameActions();
  const total = game.debris.titan + game.debris.silicate;
  if (total <= 0) return null;

  const miners = game.planets[0]!.ships.minerShip;
  const fleetCapacity = fleetCapacityStatus(game);
  return (
    <Panel title="Trümmerfeld im Orbit" eyebrow="Reste vergangener Schlachten">
      <StatGrid columns={2}>
        <Stat label="Titan" value={formatAmount(game.debris.titan)} accent="titan" size="sm" />
        <Stat label="Silikat" value={formatAmount(game.debris.silicate)} accent="silicate" size="sm" />
      </StatGrid>
      <Button
        className="mt-3"
        variant="secondary"
        size="sm"
        fullWidth
        icon={<AsteroidIcon size={14} />}
        disabled={miners < 1 || fleetCapacity.full}
        onClick={() => actions.harvestDebris(miners)}
        title={
          miners < 1
            ? 'Keine Erz-Sammelschiffe im Hangar'
            : fleetCapacity.full
              ? `Schiffshangar-Kapazität erreicht (${fleetCapacity.active}/${fleetCapacity.capacity} Flotten unterwegs)`
              : undefined
        }
      >
        {miners < 1
          ? 'Keine Sammelschiffe'
          : game.fleetMissions.some((entry) => entry.kind !== 'campAttack')
            ? 'Flotte unterwegs'
            : fleetCapacity.full
              ? 'Hangar-Kapazität erreicht'
              : `${miners} Sammelschiffe entsenden`}
      </Button>
    </Panel>
  );
}

/**
 * The live-ticker feed inside the game itself (galaxy plan Milestone D UI)
 * — deliberately at the bottom of the aside column and visually quiet
 * (muted text, no severity colours, no icons): this is ambient "the galaxy
 * is alive" texture, not something asking for the player's attention the
 * way EventPanel's alerts do. Only ever bot activity, never a real
 * player's (see apps/backend/src/activity/). Hidden entirely once there's
 * nothing to show, same as DebrisPanel above.
 */
export function ActivityFeedPanel() {
  const feed = useActivityFeed(6);
  if (feed.length === 0) return null;

  return (
    <Panel title="Galaxie-Aktivität" eyebrow="Zuletzt gesehen">
      <ul className="space-y-2">
        {feed.map((event, index) => (
          <li key={`${event.at}-${index}`} className="text-xs leading-relaxed text-content-muted">
            {event.message}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * The Ring-5 onboarding chain. Sits at the top of the aside column while it
 * runs and disappears for good once finished — a tutorial that outstays its
 * welcome is worse than none.
 */
export function MissionPanel_Onboarding() {
  const { game } = useGame();
  const mission = activeMission(game);
  if (!mission) return null;

  const step = game.missionIndex + 1;
  return (
    <Panel title={mission.title} eyebrow={`Einsatzbefehl ${step}/${MISSIONS.length}`} tone="accent" corners>
      <p className="text-xs leading-relaxed text-content-secondary">{mission.description}</p>
      <div className="mt-2 rounded-control border border-silicate/30 bg-silicate/[0.06] px-2.5 py-2">
        <span className="font-display text-label uppercase text-silicate-bright">Auftrag</span>
        <p className="mt-0.5 text-xs text-content">{mission.hint}</p>
      </div>
      <ProgressBar
        className="mt-3"
        value={game.missionIndex / MISSIONS.length}
        accent="silicate"
        size="xs"
        aria-label="Missionsfortschritt"
      />
    </Panel>
  );
}

/**
 * Floating messages: rejected commands and the events worth interrupting for.
 *
 * Opaque and self-dismissing — a translucent toast over the orbit canvas is
 * unreadable, and one that waits for a click is in the way. Errors get a longer
 * life than good news, because being told *why* an order failed is the message
 * a player most needs time to read.
 */
export function Toasts() {
  const { error, clearError, log } = useGame();
  const [dismissed, setDismissed] = useState<number[]>([]);

  // Only events the player would want pulled out of the side log get a toast.
  const notable = log
    .filter((entry) => TOASTED.has(entry.type))
    .slice(0, 3)
    .filter((entry) => !dismissed.includes(entry.at));

  return (
    <ToastStack>
      {error ? (
        <Toast
          severity="danger"
          title="Befehl abgelehnt"
          description={error}
          duration={9_000}
          onDismiss={clearError}
        />
      ) : null}

      {notable.map((entry) => (
        <Toast
          key={`${entry.at}-${entry.type}`}
          severity={
            entry.type === 'raidLost' || entry.type === 'cometImpact' || entry.type === 'scoutLost'
              ? 'danger'
              : 'success'
          }
          title={logTitle(entry.type)}
          description={entry.message}
          onDismiss={() => setDismissed((prev) => [...prev, entry.at])}
        />
      ))}
    </ToastStack>
  );
}

/** Event types loud enough to float over the game view. */
const TOASTED = new Set([
  'levelUp',
  'missionCompleted',
  'blueprint',
  'raidRepelled',
  'raidLost',
  'cometImpact',
  'expeditionReturned',
  'harvestReturned',
  'scoutLost',
]);
